import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Pill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const LISTS = ["aliases", "patterns", "upi_ids", "ref_patterns", "account_identifiers"];
const blank = { name: "", ledger_name: "", group: "Sundry Creditors", classification: "business", aliases: "", patterns: "", upi_ids: "", ref_patterns: "", account_identifiers: "", default_voucher_type: "" };

function PartyDialog({ party, onClose }) {
  const { meta, refreshMasters } = useApp();
  const [f, setF] = useState(blank);
  useEffect(() => { setF(party?.id ? { ...blank, ...party, ...Object.fromEntries(LISTS.map((k) => [k, (party[k] || []).join(", ")])), default_voucher_type: party.default_voucher_type || "" } : blank); }, [party]);
  const save = async () => {
    const body = { ...f, ...Object.fromEntries(LISTS.map((k) => [k, f[k].split(",").map((x) => x.trim()).filter(Boolean)])), default_voucher_type: f.default_voucher_type || null };
    try {
      if (party?.id) await api.patch(`/parties/${party.id}`, body); else await api.post("/parties", body);
      toast.success("Party saved");
      refreshMasters();
      onClose();
    } catch (e) { toast.error(await errMsg(e)); }
  };
  const inp = (k, ph) => <Input placeholder={ph} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`party-${k}-input`} />;
  return (
    <Dialog open={!!party} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="party-dialog">
        <DialogHeader><DialogTitle>{party?.id ? "Edit party" : "New party"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {inp("name", "Normalized party name")}{inp("ledger_name", "Confirmed Tally ledger name")}
          <Select value={f.group} onValueChange={(v) => setF({ ...f, group: v })}><SelectTrigger data-testid="party-group-select"><SelectValue /></SelectTrigger>
            <SelectContent>{meta.groups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select>
          <Select value={f.classification} onValueChange={(v) => setF({ ...f, classification: v })}><SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="business">Business</SelectItem><SelectItem value="personal">Personal</SelectItem></SelectContent></Select>
          {inp("aliases", "Confirmed aliases (comma separated)")}{inp("upi_ids", "UPI IDs / VPAs")}
          {inp("patterns", "Narration patterns (contains)")}{inp("ref_patterns", "Reference patterns")}
          {inp("account_identifiers", "Bank account identifiers")}
          <Select value={f.default_voucher_type || "auto"} onValueChange={(v) => setF({ ...f, default_voucher_type: v === "auto" ? "" : v })}><SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="auto">Voucher: by direction</SelectItem>{meta.voucher_types.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="text-xs text-muted-foreground">Only exact aliases, UPI IDs or patterns auto-match. Similar names are shown as suggestions and always need confirmation.</div>
        <Button onClick={save} data-testid="party-save-button">Save party</Button>
      </DialogContent>
    </Dialog>
  );
}

function PartySheet({ id, onClose }) {
  const [p, setP] = useState(null);
  useEffect(() => { if (id) api.get(`/parties/${id}`).then((r) => setP(r.data)); }, [id]);
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="party-sheet">
        {p && <>
          <SheetHeader><SheetTitle className="font-display">{p.name}</SheetTitle><div className="text-xs text-muted-foreground">{p.ledger_name} · {p.group} · {p.classification}</div></SheetHeader>
          <div className="mt-4 space-y-1 text-xs">{LISTS.map((k) => (p[k] || []).length > 0 && <div key={k}><span className="label-xs">{k.replace("_", " ")}:</span> {p[k].join(", ")}</div>)}</div>
          <h4 className="mt-6 text-sm font-semibold">Historical transactions ({p.transactions.length})</h4>
          {p.transactions.map((t) => (
            <div key={t.id} className="flex justify-between gap-3 border-b py-2 text-xs"><span className="num">{fmtDate(t.txn_date)}</span><span className="flex-1 truncate font-mono">{t.narration}</span>
              <span className={`num ${t.direction === "credit" ? "text-emerald-500" : "text-rose-500"}`}>{inr(t.amount)}</span><Pill s={t.status} /></div>
          ))}
          <h4 className="mt-6 text-sm font-semibold">Invoices ({p.invoices.length})</h4>
          {p.invoices.map((i) => <div key={i.id} className="flex justify-between border-b py-2 text-xs"><span>{i.invoice_no}</span><span className="num">{inr(i.amount)} · o/s {inr(i.outstanding)}</span></div>)}
        </>}
      </SheetContent>
    </Sheet>
  );
}

function Ledgers() {
  const { ledgers, meta, refreshMasters, can } = useApp();
  const [f, setF] = useState({ name: "", group: "Indirect Expenses" });
  const [q, setQ] = useState("");
  const add = async () => {
    try { await api.post("/ledgers", f); toast.success("Ledger created"); setF({ ...f, name: "" }); refreshMasters(); } catch (e) { toast.error(await errMsg(e)); }
  };
  return (
    <div>
      {can("admin", "accountant") && <div className="mb-4 flex flex-wrap gap-2">
        <Input className="w-64" placeholder="Exact Tally ledger name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="ledger-name-input" />
        <Select value={f.group} onValueChange={(v) => setF({ ...f, group: v })}><SelectTrigger className="w-56" data-testid="ledger-group-select"><SelectValue /></SelectTrigger>
          <SelectContent>{meta.groups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select>
        <Button onClick={add} data-testid="ledger-add-button">Add ledger</Button>
        <Input className="ml-auto w-56" placeholder="Filter" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>}
      <div className="panel max-h-[65vh] overflow-auto">
        <table className="w-full text-sm"><thead className="glass sticky top-0 text-left text-xs text-muted-foreground"><tr><th className="p-3">Ledger</th><th className="p-3">Group</th></tr></thead>
          <tbody>{ledgers.filter((l) => l.name.toLowerCase().includes(q.toLowerCase())).map((l) => <tr key={l.id} className="border-t" data-testid="ledger-row"><td className="p-3">{l.name}</td><td className="p-3 text-muted-foreground">{l.group}</td></tr>)}</tbody></table>
      </div>
    </div>
  );
}

export default function Parties() {
  const { parties, can } = useApp();
  const [sp] = useSearchParams();
  const [edit, setEdit] = useState(null);
  const [view, setView] = useState(sp.get("open"));
  const [q, setQ] = useState("");
  return (
    <div>
      <PageHeader title="Party & Ledger Master" sub="Learned only from your confirmed classifications. Each alias, UPI ID and pattern here was approved by a person.">
        {can("admin", "accountant") && <Button onClick={() => setEdit({})} data-testid="new-party-button"><Plus className="mr-2 h-4 w-4" />New party</Button>}
      </PageHeader>
      <Tabs defaultValue="parties">
        <TabsList><TabsTrigger value="parties" data-testid="tab-parties">Parties ({parties.length})</TabsTrigger><TabsTrigger value="ledgers" data-testid="tab-ledgers">Tally ledgers</TabsTrigger></TabsList>
        <TabsContent value="parties">
          <Input className="mb-3 w-72" placeholder="Search parties" value={q} onChange={(e) => setQ(e.target.value)} data-testid="party-search-input" />
          <div className="panel max-h-[65vh] overflow-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="glass sticky top-0 text-left text-xs text-muted-foreground"><tr>{["Party", "Ledger", "Group", "Type", "Aliases / UPI", "Txns", "Last", ""].map((h) => <th key={h} className="p-3 font-medium">{h}</th>)}</tr></thead>
              <tbody>
                {parties.filter((p) => (p.name + (p.aliases || []).join(" ")).toLowerCase().includes(q.toLowerCase())).map((p) => (
                  <tr key={p.id} className="cursor-pointer border-t transition-colors hover:bg-accent/60" onClick={() => setView(p.id)} data-testid="party-row">
                    <td className="p-3 font-medium">{p.name}</td><td className="p-3">{p.ledger_name}</td><td className="p-3 text-muted-foreground">{p.group}</td>
                    <td className="p-3 text-xs">{p.classification}</td>
                    <td className="max-w-[260px] truncate p-3 font-mono text-xs text-muted-foreground">{[...(p.aliases || []), ...(p.upi_ids || [])].join(", ")}</td>
                    <td className="num p-3">{p.txn_count}</td><td className="num p-3 text-xs">{fmtDate(p.last_txn)}</td>
                    <td className="p-3">{can("admin", "accountant") && <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setEdit(p); }} data-testid="edit-party-button">Edit</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {parties.length === 0 && <div className="p-8 text-sm text-muted-foreground">No parties yet — they are created when you confirm classifications in the review queue.</div>}
          </div>
        </TabsContent>
        <TabsContent value="ledgers"><Ledgers /></TabsContent>
      </Tabs>
      <PartyDialog party={edit} onClose={() => setEdit(null)} />
      <PartySheet id={view} onClose={() => setView(null)} />
    </div>
  );
}
